/** Shared layout pieces of the 2026-09-29 layout review: settings index, action bars, bulk actions, command menu actions. */
export const ux = {
  today: {
    title: 'To do today',
    open: 'Open',
    none: 'Nothing waiting',
    dossiers: { label: 'Dossiers to review', hint: 'Domains wait for this review before they activate' },
    work: { label: 'Open work items', hint: 'Identity checks, dossiers and exceptions' },
    expiring: { label: 'Expiring within 30 days', hint: 'Active domains to renew or remind' },
    unresolved: { label: 'Registry commands without a result', hint: 'Being reconciled; do not resend by hand' },
  },
  sidebar: {
    groups: {
      work: 'Workspace',
      commerce: 'Commerce',
      infra: 'Operations & admin',
      domains: 'Domains',
      catalog: 'Selling domains',
      registry: 'Registries & compliance',
      backorder: 'Backorder',
      auctions: 'Domain auctions',
      shared: 'Shared',
      /** The menu entries of the running add-ons (`nav/addonMenu.ts`). */
      addons: 'Add-ons',
    },
    entries: {
      home: 'Work to do',
      domains: 'Domains',
      dossiers: 'Dossiers',
      subjects: 'Registrants',
      customers: 'Customers',
      orders: 'Orders',
      invoices: 'Invoices & payments',
      ledger: 'Wallets & ledger',
      products: 'Products & prices',
      addons: 'Add-ons & fees',
      quote: 'Price calculator',
      registry: 'Registries',
      compliance: 'Compliance & VNNIC',
      ops: 'Operations & audit',
      settings: 'Settings',
      backorder: 'Backorder centre',
      drops: 'Drop list',
      backorders: 'Backorder requests',
      catching: 'Drop catching',
      holds: 'Holds & releases',
      auctions: 'Auction sessions',
      auctionSessions: 'Create & run sessions',
      bids: 'Bids & audit',
      settlement: 'Settlement',
      handover: 'Handover',
    },
  },
  addons: {
    lifecycle: 'Add-on lifecycle',
    lifecycleHint: 'An add-on has its own state, separate from the domain and from payment.',
    step: {
      order: 'Ordered',
      orderHint: 'Every add-on of this domain',
      wait: 'Waiting to apply',
      waitHint: 'Ordered, not yet in effect',
      active: 'In use',
      activeHint: 'Applied and renewed with the domain',
      end: 'Ended',
      endHint: 'Cancelled or ended',
    },
    cost: 'Renewal cost for one year',
    costHint: 'Default price book of this TLD, with fees and add-ons charged at renewal.',
    costError: 'The renewal cost could not be loaded',
    noCost: 'This TLD has no renewal price.',
    total: 'Total',
    history: 'Service history',
    noHistory: 'No add-on yet.',
    event: { ordered: '{{name}} ordered', applied: '{{name}} applied', ended: '{{name}} ended' },
    onNotDoneTitle: 'Ordered is not yet working',
    onNotDone: 'An add-on counts as in use once it is applied. DNSSEC shows as signed only when DS records are at the registry.',
  },
  dnssec: {
    loadError: 'DNSSEC could not be loaded',
    state: 'DNSSEC',
    signed: 'Signed',
    unsigned: 'Not signed',
    stateHint: 'From the DS records at the registry',
    ds: 'DS records at the registry',
    keyTags: 'Key tags',
    digest: 'Digest types',
    noKeys: 'No keys',
    chain: 'Chain of trust',
    chainHint: 'What the console knows about this domain’s signing.',
    gate: {
      tld: 'The TLD supports DNSSEC',
      tldNo: 'The registry of this TLD does not take DS records.',
      ds: 'DS records at the registry',
      dsHint: '{{n}} record(s) published in the parent zone.',
      dsNone: 'None: the domain is not signed at the parent.',
      keys: 'DNSKEY and signatures in the zone',
      keysHint: 'Checked by whoever runs the DNS; the console does not query resolvers.',
    },
  },
  privacy: {
    loadError: 'This could not be loaded',
    serviceTitle: 'WHOIS privacy',
    serviceHint: 'An add-on with its own period. Redaction required by policy applies with or without it.',
    state: 'State',
    addon: { ORDERED: 'Ordered, not yet applied', ACTIVE: 'In use', CANCELLED: 'Cancelled', ENDED: 'Ended' },
    notUsed: 'Not used on this domain',
    notOffered: 'Not offered for this TLD',
    period: 'Period',
    orderable: 'Can be ordered',
    yes: 'Yes',
    noPrice: 'No price for this customer yet',
    manage: 'Order or cancel in Add-ons & services',
    previewTitle: 'What RDAP / WHOIS publish',
    previewHint: 'The public record of this domain under the publication policy in force. Personal data is never echoed here.',
    policy: 'Policy {{key}} · version {{version}}',
    noPolicy: 'No publication policy recorded',
    notPublished: 'This domain is not in the public record.',
    col: { field: 'Field', who: 'Of', shown: 'Published as', value: 'Public value' },
    registrant: 'Registrant',
    other: 'Domain',
    shown: { PUBLIC: 'Published', REDACTED: 'Redacted', CONTACT_FORM: 'Contact form', OMITTED: 'Left out' },
    twoTitle: 'Two different records',
    internal: 'Internal record',
    internalHint: 'The real registrant, contacts and documents; shown to staff by role.',
    public: 'Public RDAP / WHOIS',
    publicHint: 'Fields allowed by the policy; the rest redacted or behind a contact form.',
    notSameTitle: 'Privacy is not the same as redaction',
    notSame: 'Redaction the policy requires is never sold as privacy. Disclosure requests are handled as cases, with a legal basis and an audit trail.',
    moreTitle: 'Related',
    linkPolicy: 'Publication policy (RDAP)',
    linkDisclosure: 'Disclosure requests',
  },
  quote: {
    title: 'Price calculator',
    subtitle: 'What one TLD’s operation costs a customer in a price book, line by line: service, pass-through fees and add-ons. Nothing is saved.',
    tld: 'TLD',
    book: 'Price book',
    defaultBook: 'Default (retail)',
    years: 'Term',
    yearsN_one: '{{count}} year',
    yearsN_other: '{{count}} years',
    operation: 'Operation',
    usedBook: 'Priced with {{book}}.',
    loadError: 'The prices could not be loaded',
    none: 'This TLD has no priced operation.',
    cannot: '{{op}} cannot be priced',
    lines: '{{op}} · {{years}} year(s)',
    linesHint: 'Mandatory fees and default add-ons of the TLD are included; optional add-ons are not.',
    noLines: 'No lines.',
    col: { item: 'Item', kind: 'Kind', amount: 'Amount', discount: 'Discount', tax: 'Tax', total: 'Total' },
    totals: 'Totals',
    registryCost: 'Our registry cost',
    subtotal: 'Before tax',
    tax: 'Tax',
    total: 'Customer pays',
    costNote: 'The registry cost is what we pay; it is not a line on the customer’s invoice.',
    feesTitle: 'Fees are separate lines',
    fees: 'State and ICANN fees are their own lines and are never discounted. A .vn registration has a one-time registration fee and a yearly maintenance fee.',
    whereTitle: 'Change a price',
    where: 'Prices, fees and which add-ons a TLD offers are set on the TLD page.',
    whereLink: 'Open the TLD’s prices',
  },
  catalogue: {
    related: 'Related pages',
    linkQuote: 'Price calculator',
    linkPrices: 'Price lists',
    linkTlds: 'TLDs',
    addons: 'Add-ons',
    addonsHint: 'Sold with a domain, per TLD',
    fees: 'Fees',
    feesHint: 'State, ICANN and operation fees',
    tlds: 'TLDs offering add-ons',
    tldsHint: 'Mandatory, default or optional',
    inUse: 'Add-ons in use',
    inUseHint: 'On domains · catalogue {{version}}',
  },
  modules: {
    label: 'Workspace',
    pick: 'Choose a module',
    platform: 'Platform & commerce',
    domains: 'Domain Registrar',
    hosting: 'Hosting cPanel',
    email: 'TinoMail Business',
    cloud: 'Cloud Instant',
    backorder: 'Backorder',
    auctions: 'Domain auctions',
  },
  settings: {
    searchLabel: 'Find a setting',
    searchPlaceholder: 'Type what you want to change: price, eKYC, e-mail, staff…',
    none: 'No setting matches “{{q}}”.',
    open: 'Open',
    groups: {
      sell: { label: 'Selling domains', description: 'TLDs, prices, promotions and services: what customers can buy and for how much.' },
      connect: { label: 'Connections', description: 'Registries, VNNIC, identity checks, e-mail and event delivery.' },
      org: { label: 'Organisation & staff', description: 'Who can sign in, what they may do and what they did.' },
      compliance: { label: 'Compliance', description: 'Documents, declaration forms, .vn rules, policies and reporting deadlines.' },
    },
    items: {
      tlds: { label: 'TLDs we sell', description: 'Each TLD with its readiness; add and set up a new TLD.' },
      channels: { label: 'TLD channels', description: 'Which connection registers each TLD.' },
      costs: { label: 'Registry costs & exchange rates', description: 'What each registration, renewal and transfer costs us.' },
      prices: { label: 'Selling prices', description: 'Tier price books and customers’ own prices per TLD.' },
      promotions: { label: 'Promotions', description: 'Discount programmes, codes and their use.' },
      services: { label: 'Domain services', description: 'Add-ons sold with a domain and their prices.' },
      tax: { label: 'Tax rates', description: 'VAT per product and period.' },
      lifecycle: { label: 'Lifecycle, transfer-back & restore', description: 'Grace periods, restore fees and DNS after expiry, per TLD.' },
      parking: { label: 'Parking DNS', description: 'Name servers used while a domain has none of its own.' },
      catalog: { label: 'TLD catalogue', description: 'Every TLD in the world, to pick a new one to sell.' },
      registries: { label: 'Registries & upstream registrars', description: 'Sign-in details, certificates and addresses of each registry connection.' },
      vnnic: { label: 'VNNIC', description: 'The VNNIC registrar connection.' },
      ekyc: { label: 'Identity checks (eKYC)', description: 'VNNIC identity verification of registrants.' },
      hstm: { label: 'Domain dossiers at VNNIC', description: 'Sending dossiers of .vn domains to VNNIC.' },
      whois: { label: 'WHOIS', description: 'The VNNIC WHOIS service and its certificate.' },
      ote: { label: 'Registry certification tests', description: 'OT&E tests each registry asks for.' },
      email: { label: 'E-mail & templates', description: 'Sending address, brand and every message template.' },
      sent: { label: 'Sent e-mails', description: 'Every message sent or waiting, with its content.' },
      webhooks: { label: 'Event delivery', description: 'Signed events sent to your own systems (e.g. a CRM).' },
      reportingApi: { label: 'Reporting API', description: 'Let Twenty CRM and internal reports read business data, read only: places, keys and use.' },
      staff: { label: 'Staff', description: 'Staff accounts, two-step sign-in and roles.' },
      roles: { label: 'Roles & permissions', description: 'What each role may do.' },
      apiKeys: { label: 'Access keys', description: 'Keys given to customers for their own systems.' },
      logRetention: { label: 'Log retention', description: 'How many days registry exchanges and every other log stay in the log store.' },
      approvals: { label: 'Approvals', description: 'Changes waiting for a second person.' },
      audit: { label: 'Audit log', description: 'Who did what, when and why.' },
      requirements: { label: 'Document sets', description: 'Documents needed for each name change or transfer case.' },
      declarations: { label: 'Declaration forms', description: 'The registrant declaration forms and their layout.' },
      vnZones: { label: '.vn zone rules', description: 'Who may register under each .vn zone.' },
      trustAnchors: { label: 'Trusted root certificates', description: 'Certificates accepted for digital signatures.' },
      documentTypes: { label: 'Document types', description: 'Domain dossier documents: who uploads them, file formats, TLDs.' },
      smartca: { label: 'SmartCA', description: "The registrar's signature with VNPT SmartCA: connection, signing service and daily time." },
      modulesRegistry: { label: 'Modules: domain names', description: 'Registries and partner registrars as modules: their connections, tests and logs.' },
      modulesPayment: { label: 'Modules: payments', description: 'Payment providers: transfer QR codes, notices of incoming money, callback address, logs.' },
      modulesNotify: { label: 'Modules: notifications', description: 'E-mail and Zalo connections used to send notices.' },
      modulesEinvoice: { label: 'Modules: e-invoices', description: 'The e-invoice provider: account, invoice template and series.' },
      modulesCaptcha: { label: 'Modules: bot protection', description: 'The "not a robot" check of the customer sign-in forms.' },
      modulesSigning: { label: 'Modules: digital signing', description: 'The registrar\'s signature service.' },
      modulesEkyc: { label: 'Modules: eKYC', description: 'Identity checks of registrants.' },
      modulesOcr: { label: 'Modules: document reading (OCR)', description: 'Reading identity documents on our own servers.' },
      modulesRegulatory: { label: 'Modules: VNNIC reporting', description: 'Reports and dossiers sent to VNNIC.' },
      modulesLookup: { label: 'Modules: domain lookup', description: 'WHOIS and availability lookups.' },
      modulesEscrow: { label: 'Modules: data escrow', description: 'Deposits of registration data with an escrow agent.' },
      modulesDns: { label: 'Modules: DNS', description: 'DNS hosting providers.' },
      modulesHosting: { label: 'Modules: hosting', description: 'Web hosting providers.' },
      modulesEmail: { label: 'Modules: business email', description: 'Business e-mail providers.' },
      modulesCloud: { label: 'Modules: cloud, VPS', description: 'Cloud server and VPS providers.' },
      modulesWebsite: { label: 'Modules: website', description: 'Website builder providers.' },
      modulesAddons: { label: 'Modules: add-ons', description: 'Extra features installed on the platform.' },
      zalo: { label: 'Notices by Zalo', description: 'Which notices also go to Zalo with which template, test sends, messages sent, numbers that receive none.' },
      captcha: { label: 'Anti-robot check', description: "The check that customers are not robots (Cloudflare Turnstile) on the client sign-in and sign-up pages: keys, when to ask, on / off." },
      declarationChecks: { label: 'Declaration checks', description: 'A declaration failing a check: staff review or automatic refusal.' },
      policies: { label: 'Policies', description: 'Compliance policies by version and effective date.' },
      rdap: { label: 'Public lookup (RDAP)', description: 'What public lookups show about a domain.' },
      deadlines: { label: 'Reporting deadlines', description: 'Reports due to VNNIC and ICANN.' },
    },
  },
  command: {
    actions: 'Actions',
    settings: 'Settings',
    registerFor: 'Register a domain for {{name}}',
    openCustomer: 'Open customer {{name}}',
    domainAction: '{{action}} · {{domain}}',
    openTld: 'Open TLD .{{zone}}',
    sellTld: 'Set up a new TLD',
    transferIn: 'Transfer a domain in from another registrar',
    domainActions: {
      restore: 'Restore',
      transferIn: 'Transfer a domain in',
      renew: 'Renew',
      nameservers: 'Change name servers',
      dnssec: 'DNSSEC',
      lock: 'Lock / unlock transfer',
      transferCode: 'Transfer code',
      registrant: 'Change registrant',
    },
  },
  actionBar: {
    more: 'More',
    related: 'Go to',
    attention: 'Needs attention',
    attentionCount_one: '{{count}} thing needs attention',
    attentionCount_other: '{{count}} things need attention',
    allClear: 'Nothing needs attention',
    fix: 'Fix',
    showAll: 'Show {{count}} more',
    showLess: 'Show less',
  },
  domain: {
    badges: {
      locked: 'Transfer locked',
      unlocked: 'Transfer unlocked',
      expires: 'Expires {{date}} · {{when}}',
      updated: 'Updated {{at}}',
    },
    status: {
      title: 'Status & constraints',
      description: 'Everything that can block an action on this domain.',
      lock: 'Transfer lock',
      autoRenew: 'Auto-renew',
      legalLocks: 'Legal locks',
      registryStatuses: 'Registry statuses',
    },
    timeline: {
      title: 'Timeline',
      all: 'Full history',
      failed: 'The history could not be loaded.',
      none: 'No events yet.',
    },
    registration: 'Registration',
    tabs: {
      overview: 'Overview',
      subject: 'Registrant & contacts',
      dossier: 'Dossier & compliance',
      dns: 'Name servers & DNSSEC',
      registry: 'Registry & history',
      billing: 'Services & billing',
    },
    row: { action: 'Action', renew: 'Renew', dossier: 'Dossier', open: 'Open' },
    views: {
      dossier: 'Dossier',
      compliance: 'Compliance',
      vnnic: 'VNNIC',
      registry: 'Registry commands',
      history: 'History',
      transfers: 'Transfers',
      audit: 'Staff actions',
      observability: 'Technical trace',
      orders: 'Orders',
      billing: 'Payments & wallet',
      invoices: 'Invoices',
      services: 'Add-ons & services',
      privacy: 'Privacy & publication',
    },
    edit: 'Edit',
    contactDrift_one: '{{count}} contact of the domain differs from its registrant now',
    contactDrift_other: '{{count}} contacts of the domain differ from their registrants now',
    cta: {
      registry: 'Open registry commands',
      compliance: 'Open compliance',
      locks: 'See the locks',
      dossier: 'Open the dossier',
      transfers: 'Open transfers',
      billing: 'Open payments',
      lifecycle: 'See expiry',
      renew: 'Renew now',
      history: 'Open history',
      contacts: 'See the contacts',
    },
    primary: {
      renew: 'Renew',
      nameservers: 'Name servers',
      lock: 'Lock transfer',
      unlock: 'Unlock transfer',
    },
  },
  bulk: {
    selectAll: 'Select every domain on this page',
    select: 'Select {{name}}',
    selected_one: '{{count}} domain selected',
    selected_other: '{{count}} domains selected',
    clear: 'Clear',
    actions: {
      renew: 'Renew',
      nameservers: 'Change name servers',
      lock: 'Lock transfer',
      unlock: 'Unlock transfer',
      syncContacts: 'Update contacts from registrant',
    },
    title: '{{action}}: {{count}} domains',
    description: 'One reason and one confirmation for all of them; each domain is sent on its own and you see the result of each.',
    years: 'Years',
    nameservers: 'Name servers (one per line, at least two)',
    nameserversHint: 'Name servers under a domain itself need addresses; change those on the domain page.',
    reason: 'Reason (kept in the audit log)',
    confirm: 'Apply to {{count}} domains',
    close: 'Close',
    retry_one: 'Retry {{count}} failed',
    retry_other: 'Retry {{count}} failed',
    running: 'Working… {{done}} / {{total}}',
    finished: 'Done: {{ok}} succeeded, {{failed}} failed.',
    status: { waiting: 'Waiting', running: 'Sending…', ok: 'Done', failed: 'Failed', skipped: 'Skipped' },
    noSubject: 'No registrant on this domain',
    upToDate: 'Already up to date',
    renewed: 'Renewal started ({{years}} years)',
    applied: 'Applied',
    pending: 'Sent to the registry',
    startedSync: 'Contacts are being updated',
    registrantChange: 'Needs a registrant change request',
  },
  views: {
    saved: 'Saved views',
    save: 'Save this view',
    namePrompt: 'Name of this view',
    remove: 'Remove view {{name}}',
    none: 'No saved views yet: filter the list, then save it.',
    local: 'Kept in this browser only',
  },
  bulkList: {
    selectAll: 'Select every row on this page',
    select: 'Select {{name}}',
    selected_one: '{{count}} selected',
    selected_other: '{{count}} selected',
    export: 'Export CSV',
    title_one: '{{action}}: {{count}} item',
    title_other: '{{action}}: {{count}} items',
    description: 'One confirmation for all of them; each item is sent on its own and you see the result of each.',
    confirm_one: 'Apply to {{count}} item',
    confirm_other: 'Apply to {{count}} items',
  },
  lists: {
    all: 'All',
    changed: 'Changed',
    unchanged: 'Already so, nothing to change',
    csv: { domains: 'Domains', currency: 'Currency' },
    customers: { status: 'Change status', newStatus: 'New status', tier: 'Change tier', newTier: 'New tier' },
    work: {
      take: 'Take',
      taken: 'Taken',
      alreadyYours: 'Already yours',
      assign: 'Assign to…',
      assignee: 'Assign to',
      assigned: 'Assigned',
      close: 'Close as handled',
      done: 'Closed',
      closed: 'Already closed',
      reviewOne: 'Reviews are decided one at a time',
      onRequest: 'Closes when its request is decided',
    },
  },
  revenue: {
    title: 'Revenue by month',
    description: 'Recognised in the ledger, before tax, net of refunds · VND · Vietnamese calendar months',
    thisMonth: 'This month ({{month}})',
    vsLast: '{{pct}} vs last month',
    noPrevious: 'No revenue last month to compare with',
    others: 'Revenue in {{list}} is not included here.',
    showTable: 'Table',
    showChart: 'Chart',
    openLedger: 'Open the ledger',
    loadError: 'Revenue could not be loaded',
    month: 'Month',
    amount: 'Revenue',
    chartLabel: 'Revenue per month over the last 12 months',
  },
  customer: {
    domainsBadge_one: '{{count}} domain',
    domainsBadge_other: '{{count}} domains',
    dossierCol: 'Dossiers',
    dossierOpen_one: '{{count}} incomplete',
    dossierOpen_other: '{{count}} incomplete',
    dossierDone: 'Complete',
    tabs: { account: 'Account details', domains: 'Domains ({{count}})', subjects: 'Registrants & dossiers ({{count}})', users: 'Users ({{count}})', billing: 'Billing & wallet' },
    primary: { register: 'Register a domain', newService: 'New service', newSubject: 'New registrant', edit: 'Edit' },
    more: { addUser: 'Add a sign-in user' },
    attention: {
      suspended: 'The account is not active: new registrations are blocked.',
      unverified_one: '{{count}} registrant is not verified yet.',
      unverified_other: '{{count}} registrants are not verified yet.',
      dossiers_one: '{{count}} domain has an incomplete dossier.',
      dossiers_other: '{{count}} domains have an incomplete dossier.',
      expiring_one: '{{count}} domain expires within 30 days.',
      expiring_other: '{{count}} domains expire within 30 days.',
      stopped_one: '{{count}} domain is not active (stopped, expired or failed).',
      stopped_other: '{{count}} domains are not active (stopped, expired or failed).',
      lowWallet: 'The wallet has no available balance.',
      openSubjects: 'Open registrants',
      openDomains: 'Open domains',
      openBilling: 'Open wallet',
      edit: 'Edit account',
    },
  },
} as const;
